import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Minus, Plus, ShoppingBag, Truck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import ShareButtons from "@/components/ShareButtons";
import { useCart } from "@/context/CartContext";
import { getProduct, PRODUCTS, DELIVERY } from "@/data/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ACCENT_BG = {
  water: "bg-water-soft",
  jasmin: "bg-jasmin",
  mandariini: "bg-mandariini",
};

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const buyRef = useRef(null);

  useEffect(() => {
    setQty(1);
    setActiveImg(0);
  }, [slug]);

  useEffect(() => {
    const el = buyRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => setShowSticky(!entries[0].isIntersecting), {
      threshold: 0,
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [slug]);

  if (!product) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="product-not-found">
        <h1 className="h-section font-heading font-semibold">Tuotetta ei löytynyt</h1>
        <Link to="/kauppa/" className="mt-6 inline-flex items-center gap-2 text-primary font-bold">
          Takaisin kauppaan <ArrowRight size={16} />
        </Link>
      </main>
    );
  }

  const others = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 2);

  const addToCart = () => {
    add(slug, qty);
    toast.success(`${product.name} lisätty ostoskoriin`);
  };

  const accordionSections = [
    { id: "kuvaus", title: "Kuvaus", content: product.description },
    product.ingredients && {
      id: "ainesosat",
      title: "Ihonhoidolliset ainesosat",
      list: product.ingredients.map(
        (ing) => `${ing.name} – ${ing.points.join(". ").toLowerCase()}`
      ),
    },
    product.materials && { id: "materiaalit", title: "Materiaalit", list: product.materials },
    {
      id: "tekniset",
      title: "Tekniset tiedot",
      specs: product.specs,
    },
    { id: "kenelle", title: "Kenelle tuote sopii?", list: product.targetGroups },
    {
      id: "asennus",
      title: "Asennus ja vaihto",
      list: [
        "Sopii kaikkiin Suomessa käytössä oleviin hanoihin ja suihkuihin – asennus ilman työkaluja",
        product.slug === "jinsu-suodatin"
          ? "Suositeltu käyttöikä n. 30–60 päivää (veden laadusta ja käytöstä riippuen)"
          : product.slug === "jinsu-suihkupaa"
            ? "Ei vaihdettavia osia – huuhtele säännöllisesti puhtaaksi"
            : "Riittoisuus n. 7 200 litraa (n. 30 päivää / 4 hengen talous)",
        product.slug === "jinsu-suihkupaa"
          ? "Kiinnittyy tavalliseen suihkuletkuun muutamassa minuutissa"
          : "Läpinäkyvä runko helpottaa suodattimen vaihtotarpeen seuraamista",
      ],
    },
  ].filter(Boolean);

  return (
    <main data-testid="product-page" className="pt-24 md:pt-32 pb-24">
      <Seo
        title={`${product.name} – ${product.priceLabel} | JINSU`}
        description={product.short}
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <nav className="text-xs font-semibold text-muted-foreground" aria-label="Murupolku">
          <Link to="/" className="hover:text-primary">Etusivu</Link>
          <span className="mx-2">/</span>
          <Link to="/kauppa/" className="hover:text-primary">Kauppa</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{product.name}</span>
        </nav>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          {/* Gallery */}
          <div className="lg:sticky lg:top-28 self-start">
            <div
              className={`overflow-hidden rounded-[2rem] ${ACCENT_BG[product.accent]} shadow-xl shadow-primary/10`}
            >
              <img
                src={product.images[activeImg]}
                alt={product.name}
                className="w-full aspect-[4/5] object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setActiveImg(i)}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`Kuva ${i + 1}`}
                    className={`h-20 w-20 overflow-hidden rounded-xl transition-all duration-200 ${
                      i === activeImg ? "ring-2 ring-primary" : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1
              className="font-heading font-semibold text-foreground leading-tight"
              style={{ fontSize: "clamp(1.9rem, 3.2vw + 0.6rem, 3.2rem)" }}
              lang="fi"
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-muted-foreground">
              {product.short}
            </p>

            <div className="mt-7 flex flex-wrap items-end gap-3">
              <p className="text-3xl font-extrabold text-foreground" data-testid="product-price">
                {product.priceLabel}
              </p>
              {product.compareAtLabel && (
                <p className="pb-1 text-base text-muted-foreground line-through" data-testid="product-compare-price">
                  {product.compareAtLabel}
                </p>
              )}
              <p className="pb-1 text-xs text-muted-foreground">
                {product.saveLabel ? `${product.saveLabel} · ` : ""}sis. ALV, toimituskulut lisätään kassalla
              </p>
            </div>

            {product.inStock && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-water-soft px-4 py-1.5 text-xs font-bold text-water-deep" data-testid="product-stock">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Varastossa
              </p>
            )}

            {/* Buy box */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-full border border-border bg-white" data-testid="quantity-stepper">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="flex h-12 w-12 items-center justify-center text-foreground transition-colors hover:text-primary"
                  aria-label="Vähennä määrää"
                  data-testid="qty-decrease"
                >
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center text-sm font-extrabold" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="flex h-12 w-12 items-center justify-center text-foreground transition-colors hover:text-primary"
                  aria-label="Lisää määrää"
                  data-testid="qty-increase"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                ref={buyRef}
                onClick={addToCart}
                data-product-slug={product.slug}
                data-testid="add-to-cart-button"
                className="inline-flex h-12 flex-1 min-w-[13rem] items-center justify-center gap-2.5 rounded-full bg-primary px-7 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-[1.03]"
              >
                <ShoppingBag size={17} /> Lisää ostoskoriin
              </button>
            </div>

            {/* Benefits */}
            <ul className="mt-9 space-y-3">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-foreground/85">
                  <Check size={17} className="mt-0.5 shrink-0 text-water-deep" strokeWidth={3} />
                  {b}
                </li>
              ))}
            </ul>

            {/* Delivery */}
            <div className="mt-9 flex items-start gap-4 rounded-3xl bg-water-soft p-6" data-testid="delivery-info">
              <Truck size={22} className="mt-0.5 shrink-0 text-water-deep" strokeWidth={1.8} />
              <div className="text-sm leading-relaxed text-muted-foreground">
                <p className="font-bold text-foreground">Toimitus Postilla kaikkialle Suomeen</p>
                <p className="mt-1">{DELIVERY.processing} {DELIVERY.time}</p>
              </div>
            </div>

            <ShareButtons productName={product.name} />

            {/* Details accordion */}
            <div className="mt-10" data-testid="product-details">
              <Accordion type="single" collapsible defaultValue="kuvaus">
                {accordionSections.map((s) => (
                  <AccordionItem key={s.id} value={s.id} data-testid={`product-section-${s.id}`}>
                    <AccordionTrigger className="text-left font-heading text-lg font-semibold">
                      {s.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      {s.content && (
                        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                          {s.content.map((p, i) => (
                            <p key={i}>{p}</p>
                          ))}
                        </div>
                      )}
                      {s.list && (
                        <ul className="space-y-2.5 text-sm leading-relaxed text-muted-foreground">
                          {s.list.map((li, i) => (
                            <li key={i} className="flex gap-2.5">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-water-deep/60" />
                              {li}
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.specs && (
                        <dl className="divide-y divide-border text-sm">
                          {s.specs.map((sp) => (
                            <div key={sp.label} className="grid grid-cols-3 gap-4 py-3">
                              <dt className="font-bold text-foreground">{sp.label}</dt>
                              <dd className="col-span-2 text-muted-foreground">{sp.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>

        {/* Cross-sell */}
        <section className="mt-24 md:mt-32" data-testid="cross-sell">
          <h2 className="h-section font-heading font-semibold text-foreground" lang="fi">
            Tutustu myös näihin
          </h2>
          <div className="mt-10 grid gap-7 md:grid-cols-2 lg:max-w-4xl">
            {others.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      </div>

      {/* Sticky mobile add-to-cart bar */}
      <div
        data-testid="sticky-buy-bar"
        className={`fixed inset-x-0 bottom-0 z-40 glass border-t border-white/50 px-5 py-3.5 transition-transform duration-300 lg:hidden ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-xs font-bold text-foreground">{product.name}</p>
            <p className="text-sm font-extrabold text-primary">{product.priceLabel}</p>
          </div>
          <button
            onClick={addToCart}
            data-product-slug={product.slug}
            data-testid="sticky-add-to-cart"
            className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground"
          >
            <ShoppingBag size={16} /> Lisää koriin
          </button>
        </div>
      </div>
    </main>
  );
}
